import type { RoleKey, TeamMember } from '@qlk/shared';
import { ROLE_KEYS, ROLE_LABEL } from '@qlk/shared';
import { useState } from 'react';

import { coQuyen, useToi } from '../../../lib/auth.js';
import { Avatar, HopThoai, Nut, O, OChon, StatusBadge } from '../../../ui/index.js';
import { useCapNhatThanhVien, useDanhSachKenhGon, useDatLaiMatKhau, useGiaoKenh } from '../api.js';
import { KhoiGoThanhVien } from './KhoiGoThanhVien.js';
import { ThongBaoMail } from './KhoiLinkDatMatKhau.js';
import { ONhieuKenh } from './ONhieuKenh.js';

const DANG_EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export interface DialogThanhVienProps {
  thanhVien: TeamMember;
  /** Dự phòng khi không có quyền đọc danh sách kênh của module 01. */
  kenhDuPhong: { id: string; name: string }[];
  onDong: () => void;
}

export function DialogThanhVien({ thanhVien, kenhDuPhong, onDong }: DialogThanhVienProps) {
  const toi = useToi();
  const danhSachKenh = useDanhSachKenhGon();
  const capNhat = useCapNhatThanhVien();
  const giaoKenh = useGiaoKenh();
  const datLai = useDatLaiMatKhau();

  const [roleKey, setRoleKey] = useState<RoleKey>(thanhVien.roles[0] ?? 'collaborator');
  const [chon, setChon] = useState<string[]>(thanhVien.channels.map((k) => k.id));
  // Đổi email phải nhập hai lần, như ở "Tài khoản của tôi".
  const [email, setEmail] = useState('');
  const [xacNhanEmail, setXacNhanEmail] = useState('');

  const kenh =
    danhSachKenh.data?.items.map((k) => ({ id: k.id, name: k.name })) ??
    (kenhDuPhong.length > 0
      ? kenhDuPhong
      : thanhVien.channels.map((k) => ({ id: k.id, name: k.name })));

  const emailSach = email.trim().toLowerCase();
  const xacNhanSach = xacNhanEmail.trim().toLowerCase();
  const doiEmail = emailSach.length > 0;

  const emailSaiDang = doiEmail && !DANG_EMAIL.test(emailSach);
  const emailLechNhau = xacNhanSach.length > 0 && xacNhanSach !== emailSach;
  const emailTrungHienTai = doiEmail && emailSach === thanhVien.email.toLowerCase();
  const emailChuaXong =
    doiEmail && (emailSaiDang || emailTrungHienTai || xacNhanSach !== emailSach);

  const laChinhMinh = toi.data?.user.id === thanhVien.id;
  const laAdmin = coQuyen(toi.data?.permissions, 'team', 'admin');

  const dangLuu = capNhat.isPending || giaoKenh.isPending;
  const loi = capNhat.error?.message ?? giaoKenh.error?.message ?? datLai.error?.message ?? null;

  function doiKenh(id: string) {
    setChon((truoc) => (truoc.includes(id) ? truoc.filter((x) => x !== id) : [...truoc, id]));
  }

  const nhanTrangThai =
    thanhVien.status === 'disabled'
      ? { tone: 'bad' as const, chu: 'Đã khoá' }
      : thanhVien.status === 'invited'
        ? { tone: 'warn' as const, chu: 'Đã mời' }
        : null;

  /**
   * Lưu **không** chạm vào trạng thái tài khoản: khoá, lưu trữ và xoá đi qua nút
   * riêng trong khối "Trạng thái tài khoản" bên dưới, mỗi việc một hộp xác nhận
   * và một dòng nhật ký riêng.
   */
  async function luu() {
    await capNhat.mutateAsync({
      id: thanhVien.id,
      roleKeys: [roleKey],
      ...(doiEmail ? { email: emailSach, confirmEmail: xacNhanSach } : {}),
    });
    await giaoKenh.mutateAsync({ id: thanhVien.id, channelIds: chon, role: 'member' });
    onDong();
  }

  return (
    <HopThoai
      title="Sửa thành viên"
      rong="md"
      onDong={onDong}
      dauHop={
        <div className="flex items-center gap-3">
          <Avatar name={thanhVien.name} color={thanhVien.avatarColor} size={40} />
          <div className="min-w-0">
            <h2 className="truncate text-tieu-de-hop font-bold text-chu">{thanhVien.name}</h2>
            <p className="truncate text-phu text-chu-phu">{thanhVien.email}</p>
          </div>
          {nhanTrangThai && (
            <StatusBadge tone={nhanTrangThai.tone}>{nhanTrangThai.chu}</StatusBadge>
          )}
        </div>
      }
      chanHop={
        <>
          {loi && (
            <p role="alert" className="mr-auto self-center text-phu text-bad-chu">
              {loi}
            </p>
          )}
          <Nut kieu="phu" onClick={onDong}>
            Huỷ
          </Nut>
          <Nut
            kieu="chinh"
            disabled={emailChuaXong}
            dangChay={dangLuu}
            onClick={() => {
              void luu();
            }}
          >
            Lưu
          </Nut>
        </>
      }
    >
      <div className="space-y-4">
        <OChon
          hienNhan
          label="Vai trò"
          value={roleKey}
          onChange={(v) => {
            setRoleKey(v as RoleKey);
          }}
          options={ROLE_KEYS.map((k) => ({ value: k, label: ROLE_LABEL[k] }))}
          className="w-full"
        />

        <O
          label="Email đăng nhập mới"
          type="email"
          autoComplete="off"
          value={email}
          onChange={setEmail}
          moTa={
            emailSaiDang
              ? 'Email không hợp lệ'
              : emailTrungHienTai
                ? 'Email mới trùng email hiện tại'
                : 'Để trống nếu không đổi. Đổi xong, mọi phiên của người này bị đăng xuất.'
          }
        />

        {doiEmail && (
          <O
            label="Nhập lại email mới"
            type="email"
            autoComplete="off"
            value={xacNhanEmail}
            onChange={setXacNhanEmail}
            moTa={emailLechNhau ? 'Hai lần nhập email không khớp' : undefined}
          />
        )}

        <fieldset>
          <legend className="mb-2 text-phu font-medium text-chu-phu">Kênh được giao</legend>
          <ONhieuKenh kenh={kenh} chon={chon} onDoi={doiKenh} onDatLai={setChon} />
        </fieldset>
      </div>

      <div className="mt-5 rounded-[10px] border border-vien bg-nen p-3">
        <p className="text-phu font-medium text-chu-phu">Mật khẩu</p>
        <p className="mt-1 text-phu text-chu-mo">
          Hệ thống gửi link đặt mật khẩu tới {thanhVien.email}. Mật khẩu không hiện ở đây và không
          ai đọc được, kể cả quản trị.
        </p>

        {datLai.data ? (
          <ThongBaoMail ketQua={datLai.data} tieuDe="Đã tạo link đặt lại mật khẩu" />
        ) : (
          <Nut
            kieu="phu"
            className="mt-3"
            dangChay={datLai.isPending}
            onClick={() => {
              datLai.mutate({ id: thanhVien.id });
            }}
          >
            Đặt lại mật khẩu
          </Nut>
        )}
      </div>

      {/*
        "Vùng nguy hiểm" ở **cuối** thân hộp: thao tác phá hoại nằm sau mọi ô
        sửa bình thường, phải cuộn tới mới thấy.

        Người "đã mời" không có khối này: việc của họ là **thu hồi lời mời** (nút
        trên thẻ thành viên), không phải khoá hay lưu trữ một tài khoản chưa ai
        dùng.

        Đổi xong thì đóng hẳn hộp thoại (`onXong`): `thanhVien` là bản chụp lúc
        mở, giữ hộp mở sẽ hiện trạng thái cũ.
      */}
      {laAdmin && thanhVien.status !== 'invited' && (
        <KhoiGoThanhVien thanhVien={thanhVien} laChinhMinh={laChinhMinh} onXong={onDong} />
      )}
    </HopThoai>
  );
}
